import { useDatabase } from "@/hooks/useDatabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Search, Star, Calendar, Users, FlaskConical, LayoutDashboard, Beaker, BookOpen, Network } from "lucide-react";
import type { Study, Mushroom, Intervention } from "@/types/database";
import FilterBar from "@/components/FilterBar";
import MolecularBackdrop from "@/components/MolecularBackdrop";
import GlobalSearch from "@/components/GlobalSearch";
import ShareButton from "@/components/ShareButton";

export default function Home() {
  const { database, loading } = useDatabase();
  const studies = database?.studies || [];
  const mushrooms = database?.mushrooms || [];
  const interventions = database?.interventions || [];
  
  const [location, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMushroom, setSelectedMushroom] = useState<string>("all");
  const [selectedQuality, setSelectedQuality] = useState<number>(0);
  const [selectedYear, setSelectedYear] = useState<string>("all");
  
  // Advanced filters
  const [dosageRange, setDosageRange] = useState<string>("any");
  const [studyPopulation, setStudyPopulation] = useState<string>("any");
  const [duration, setDuration] = useState<string>("any");
  const [studyType, setStudyType] = useState<string>("all");
  const [qualityScore, setQualityScore] = useState<string>("all");
  const [yearRange, setYearRange] = useState<string>("all");
  const [sampleSize, setSampleSize] = useState<string>("any");

  // Load filters from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dosage')) setDosageRange(params.get('dosage')!);
    if (params.get('population')) setStudyPopulation(params.get('population')!);
    if (params.get('duration')) setDuration(params.get('duration')!);
    if (params.get('type')) setStudyType(params.get('type')!);
    if (params.get('quality')) setQualityScore(params.get('quality')!);
    if (params.get('year')) setYearRange(params.get('year')!);
    if (params.get('sample')) setSampleSize(params.get('sample')!);
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (dosageRange !== 'any') params.set('dosage', dosageRange);
    if (studyPopulation !== 'any') params.set('population', studyPopulation);
    if (duration !== 'any') params.set('duration', duration);
    if (studyType !== 'all') params.set('type', studyType);
    if (qualityScore !== 'all') params.set('quality', qualityScore);
    if (yearRange !== 'all') params.set('year', yearRange);
    if (sampleSize !== 'any') params.set('sample', sampleSize);
    
    const newSearch = params.toString();
    const newUrl = newSearch ? `/?${newSearch}` : '/';
    if (newUrl !== location) {
      window.history.replaceState({}, '', newUrl);
    }
    
    // Save to localStorage
    localStorage.setItem('mushroom_filters', JSON.stringify({
      dosageRange, studyPopulation, duration, studyType, qualityScore, yearRange, sampleSize
    }));
  }, [dosageRange, studyPopulation, duration, studyType, qualityScore, yearRange, sampleSize, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F4F2EC] via-white to-[#F4F2EC]">
        <div className="container py-12">
          <Skeleton className="h-32 w-full mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const filteredStudies = studies.filter((study: Study) => {
    const matchesSearch = study.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         study.authors.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         study.abstract.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesQuality = selectedQuality === 0 || study.quality_score >= selectedQuality;
    const matchesYear = selectedYear === "all" || study.year.toString() === selectedYear;
    
    // Advanced filter: Study Type
    const matchesStudyType = studyType === "all" || study.study_type === studyType;
    
    // Advanced filter: Quality Score
    const matchesQualityFilter = qualityScore === "all" || study.quality_score >= Number(qualityScore);
    
    // Advanced filter: Duration (parse from study duration field if available)
    let matchesDuration = true;
    if (duration !== "any" && study.duration) {
      const durationWeeks = parseInt(study.duration);
      if (duration === "<4weeks") matchesDuration = durationWeeks < 4;
      else if (duration === "4-12weeks") matchesDuration = durationWeeks >= 4 && durationWeeks <= 12;
      else if (duration === ">12weeks") matchesDuration = durationWeeks > 12;
    }
    
    // Advanced filter: Dosage Range (parse from interventions)
    let matchesDosage = true;
    if (dosageRange !== "any") {
      const intervention = interventions.find((i: Intervention) => i.study_id === study.study_id);
      if (intervention && intervention.dosage) {
        const dosageMg = parseInt(intervention.dosage);
        if (dosageRange === "<500mg") matchesDosage = dosageMg < 500;
        else if (dosageRange === "500-1000mg") matchesDosage = dosageMg >= 500 && dosageMg <= 1000;
        else if (dosageRange === "1-3g") matchesDosage = dosageMg >= 1000 && dosageMg <= 3000;
        else if (dosageRange === ">3g") matchesDosage = dosageMg > 3000;
      }
    }
    
    // Advanced filter: Study Population (infer from conditions or sample)
    let matchesPopulation = true;
    if (studyPopulation !== "any") {
      // Simple heuristic: if study mentions "healthy" it's healthy population
      const isHealthy = study.title.toLowerCase().includes('healthy') || 
                       study.abstract.toLowerCase().includes('healthy volunteers');
      if (studyPopulation === "healthy") matchesPopulation = isHealthy;
      else if (studyPopulation === "diseased") matchesPopulation = !isHealthy;
    }
    
    // Advanced filter: Year Range
    let matchesYearRange = true;
    if (yearRange !== "all") {
      const year = study.year;
      if (yearRange === "2020-2025") matchesYearRange = year >= 2020 && year <= 2025;
      else if (yearRange === "2015-2019") matchesYearRange = year >= 2015 && year <= 2019;
      else if (yearRange === "2010-2014") matchesYearRange = year >= 2010 && year <= 2014;
      else if (yearRange === "2000-2009") matchesYearRange = year >= 2000 && year <= 2009;
      else if (yearRange === "<2000") matchesYearRange = year < 2000;
    }
    
    // Advanced filter: Sample Size
    let matchesSampleSize = true;
    if (sampleSize !== "any" && study.sample_size) {
      const size = study.sample_size;
      if (sampleSize === "<50") matchesSampleSize = size < 50;
      else if (sampleSize === "50-100") matchesSampleSize = size >= 50 && size <= 100;
      else if (sampleSize === "100-500") matchesSampleSize = size >= 100 && size <= 500;
      else if (sampleSize === ">500") matchesSampleSize = size > 500;
    }
    
    const intervention = interventions.find((i: Intervention) => i.study_id === study.study_id);
    const matchesMushroom = selectedMushroom === "all" || intervention?.mushroom_id === selectedMushroom;
    
    return matchesSearch && matchesQuality && matchesYear && matchesStudyType && 
           matchesQualityFilter && matchesDuration && matchesDosage && matchesPopulation && 
           matchesYearRange && matchesSampleSize && matchesMushroom;
  });

  const years = Array.from(new Set(studies.map((s: Study) => s.year))).sort((a, b) => b - a);

  const getMushroomForStudy = (studyId: string) => {
    const intervention = interventions.find((i: Intervention) => i.study_id === studyId);
    return mushrooms.find((m: Mushroom) => m.mushroom_id === intervention?.mushroom_id);
  };

  const handleClearAllFilters = () => {
    setSearchTerm("");
    setSelectedMushroom("all");
    setSelectedQuality(0);
    setSelectedYear("all");
    setDosageRange("any");
    setStudyPopulation("any");
    setDuration("any");
    setStudyType("all");
    setQualityScore("all");
    setYearRange("all");
    setSampleSize("any");
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Hero Section — editorial cream */}
      <div className="bg-gradient-to-b from-[#FAFAF8] to-[#F4F2EC] border-b border-[#E4E1D8] relative overflow-hidden">
        <div className="container py-20 relative z-10">
          <div className="max-w-4xl">
            <div className="eyebrow mb-4">FungAlchemy · Clinical Research</div>
            <h1 className="text-6xl font-light mb-6 tracking-tight text-[#2A2A28]" style={{ fontFamily: "'Spectral', Georgia, serif" }}>
              Mushroom Clinical Research Database
            </h1>
            <p className="text-xl text-[#6E6B64] mb-8 leading-relaxed">
              Comprehensive knowledge base of <span className="font-mono font-medium text-[#A98A2C]">{studies.length}</span> clinical trials across <span className="font-mono font-medium text-[#A98A2C]">{mushrooms.filter((m: Mushroom) => m.total_studies > 0).length}</span> medicinal mushroom species
            </p>
            
            {/* Global Search Bar */}
            <div className="mb-10">
              <GlobalSearch />
            </div>
            <div className="flex flex-wrap gap-4">
              <Link href="/dashboard">
                <Button
                  size="lg"
                  className="gap-2 bg-[#C8A53D] hover:bg-[#C8A53D] text-white px-8 py-6 text-base font-semibold rounded-lg shadow-[0_4px_12px_rgba(200, 165, 61,0.4)] hover:shadow-[0_6px_16px_rgba(200, 165, 61,0.5)] transition-all duration-300 hover:-translate-y-1"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Dashboard
                </Button>
              </Link>
              <Link href="/knowledge-graph">
                <Button
                  size="lg"
                  className="gap-2 bg-[#C26A1E] hover:bg-[#A85818] text-white px-8 py-6 text-base font-semibold rounded-lg shadow-[0_4px_12px_rgba(194, 106, 30,0.4)] hover:shadow-[0_6px_16px_rgba(194, 106, 30,0.5)] transition-all duration-300 hover:-translate-y-1"
                >
                  <Network className="w-5 h-5" />
                  Knowledge Graph
                </Button>
              </Link>
              <Link href="/extraction">
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2 bg-transparent border border-[#A98A2C] text-[#A98A2C] hover:bg-[#A98A2C]/8 px-8 py-6 text-base font-medium rounded-md transition-all duration-300 hover:-translate-y-1"
                >
                  <Beaker className="w-5 h-5" />
                  Extraction Science
                </Button>
              </Link>
              <Link href="/compounds">
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2 bg-transparent border border-[#A98A2C] text-[#A98A2C] hover:bg-[#A98A2C]/8 px-8 py-6 text-base font-medium rounded-md transition-all duration-300 hover:-translate-y-1"
                >
                  <FlaskConical className="w-5 h-5" />
                  Compound Library
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-12">
        {/* Search and Filters */}
        <Card className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(42, 42, 40,0.08)] hover:shadow-[0_4px_16px_rgba(42, 42, 40,0.12)] transition-shadow rounded-xl mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Search Clinical Trials
            </CardTitle>
            <CardDescription>Filter by species, quality score, and publication year</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Advanced Filters */}
            <FilterBar
              dosageRange={dosageRange}
              studyPopulation={studyPopulation}
              duration={duration}
              studyType={studyType}
              qualityScore={qualityScore}
              yearRange={yearRange}
              sampleSize={sampleSize}
              onDosageChange={setDosageRange}
              onPopulationChange={setStudyPopulation}
              onDurationChange={setDuration}
              onStudyTypeChange={setStudyType}
              onQualityScoreChange={setQualityScore}
              onYearRangeChange={setYearRange}
              onSampleSizeChange={setSampleSize}
              onClearAll={handleClearAllFilters}
            />
            
            <div className="border-t pt-4" />
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by title, authors, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Mushroom Species</label>
                <select
                  value={selectedMushroom}
                  onChange={(e) => setSelectedMushroom(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="all">All Species</option>
                  {mushrooms
                    .filter((m: Mushroom) => m.total_studies > 0)
                    .sort((a: Mushroom, b: Mushroom) => b.total_studies - a.total_studies)
                    .map((mushroom: Mushroom) => (
                      <option key={mushroom.mushroom_id} value={mushroom.mushroom_id}>
                        {mushroom.common_name} ({mushroom.total_studies})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Minimum Quality Score</label>
                <select
                  value={selectedQuality}
                  onChange={(e) => setSelectedQuality(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value={0}>All Quality Levels</option>
                  <option value={5}>⭐⭐⭐⭐⭐ (5 stars)</option>
                  <option value={4}>⭐⭐⭐⭐ (4+ stars)</option>
                  <option value={3}>⭐⭐⭐ (3+ stars)</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Publication Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="all">All Years</option>
                  {years.map(year => (
                    <option key={year} value={year.toString()}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-gray-600">
                Showing {filteredStudies.length} of {studies.length} studies
              </p>
              <div className="flex items-center gap-2">
                <ShareButton resultCount={filteredStudies.length} />
                {(searchTerm || selectedMushroom !== "all" || selectedQuality > 0 || selectedYear !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedMushroom("all");
                      setSelectedQuality(0);
                      setSelectedYear("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Studies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStudies.map((study: Study) => {
            const mushroom = getMushroomForStudy(study.study_id);
            return (
              <Link key={study.study_id} href={`/study/${study.study_id}`}>
                <Card className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(42, 42, 40,0.08)] hover:shadow-[0_8px_24px_rgba(42, 42, 40,0.15)] transition-all duration-300 hover:-translate-y-1 cursor-pointer h-full flex flex-col rounded-xl overflow-hidden">
                  {/* Royal blue accent line */}
                  <div className="h-1 bg-gradient-to-r from-[#A98A2C] to-[#C8A53D]" />
                  <CardHeader className="p-6">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Badge className="text-xs bg-[#F4F2EC] text-[#A98A2C] border-0 font-medium">
                        {mushroom?.common_name || "Unknown"}
                      </Badge>
                      <div className="flex items-center gap-1 text-[#F59E0B]">
                        {[...Array(study.quality_score)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-current" />
                        ))}
                      </div>
                    </div>
                    <CardTitle className="text-lg line-clamp-2">{study.title}</CardTitle>
                    <CardDescription className="text-sm">
                      {study.authors.split(',')[0]} et al.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-between">
                    <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                      {study.key_finding_summary}
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>{study.year}</span>
                        <span>•</span>
                        <span className="uppercase font-medium">{study.study_type}</span>
                      </div>
                      {study.sample_size && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Users className="w-4 h-4" />
                          <span>n = {study.sample_size}</span>
                        </div>
                      )}
                      {study.pmid !== "Not specified" && (
                        <div className="flex items-center gap-2 text-xs text-[#A98A2C] font-medium">
                          <BookOpen className="w-4 h-4" />
                          <span>PubMed: {study.pmid}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {filteredStudies.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No studies found matching your criteria.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setSearchTerm("");
                setSelectedMushroom("all");
                setSelectedQuality(0);
                setSelectedYear("all");
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
